import Compressor from 'compressorjs';

/**
 * 根据图片URL获取Blob对象
 * @param {string} imageUrl - 图片URL
 * @returns {Promise<Blob>}
 */
export const getImageBlobFromUrl = async (imageUrl) => {
    try {
        const response = await fetch(imageUrl);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.blob();
    } catch (error) {
        console.error('获取图片Blob失败:', error);
        throw error;
    }
};

/**
 * 压缩图片
 * @param {Blob} file - 图片Blob对象
 * @param {Object} options - 压缩选项
 * @returns {Promise<string>} 返回压缩后的图片URL
 */
export const compressImage = (file, options = {}) => {
    return new Promise((resolve, reject) => {
        const defaultOptions = {
            quality: 0.6,
            maxWidth: 1920,
            maxHeight: 1080,
            convertSize: 1000000,
            success(result) {
                const compressedUrl = URL.createObjectURL(result);
                resolve(compressedUrl);
            },
            error(err) {
                reject(err);
            }
        };

        new Compressor(file, { ...defaultOptions, ...options });
    });
};

/**
 * 根据图片URL压缩图片
 * @param {string} imageUrl - 图片URL
 * @param {Object} options - 压缩选项
 * @returns {Promise<string>} 返回压缩后的图片URL
 */
export const compressImageFromUrl = async (imageUrl, options = {}) => {
    try {
        const blob = await getImageBlobFromUrl(imageUrl);
        return await compressImage(blob, options);
    } catch (error) {
        console.error('图片压缩失败:', error);
        throw error;
    }
};

/**
 * 批量压缩图片
 * @param {Array} imageList - 图片列表，每个元素包含id和imageUrl
 * @param {Object} options - 压缩选项
 * @returns {Promise<Array>} 返回包含压缩后URL的图片列表
 */
export const batchCompressImages = async (imageList, options = {}) => {
    const compressPromises = imageList.map(async (item) => {
        try {
            const compressedUrl = await compressImageFromUrl(item.imageUrl, options);
            return {
                ...item,
                compressedUrl,
                compressSuccess: true
            };
        } catch (error) {
            console.error(`图片 ${item.id} 压缩失败:`, error);
            return {
                ...item,
                compressedUrl: item.imageUrl, // 失败时使用原图
                compressSuccess: false
            };
        }
    });

    return await Promise.all(compressPromises);
};

/**
 * 清理Blob URL，避免内存泄漏
 * @param {string} blobUrl - Blob URL
 */
export const revokeBlobUrl = (blobUrl) => {
    if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
    }
};

/**
 * 批量清理Blob URL
 * @param {Array} blobUrls - Blob URL数组
 */
export const revokeBlobUrls = (blobUrls) => {
    blobUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    });
};

export default {
    getImageBlobFromUrl,
    compressImage,
    compressImageFromUrl,
    batchCompressImages,
    revokeBlobUrl,
    revokeBlobUrls
};